import { useState } from "react"
import { Pledge, PledgeCampaign, GivingPerson } from "../types"
import { Icon } from "source/shared/components"
import { css } from "@emotion/react"
import PledgeDetails from "./PledgeDetails"

interface PledgesListProps {
  pledges: Pledge[]
  pledgeCampaigns: PledgeCampaign[]
  jointGiver: GivingPerson | null
  showFullDetails: boolean
}

export default function PledgesList({
  pledges,
  pledgeCampaigns,
  jointGiver,
  showFullDetails,
}: PledgesListProps) {
  const [showConcludedPledges, setShowConcludedPledges] = useState(false)

  const currentPledgeCampaigns = pledgeCampaigns.filter(
    (campaign) => new Date(campaign.ends_at) > new Date(),
  )

  const currentPledges = pledges.filter((pledge) =>
    currentPledgeCampaigns.some(
      (campaign) => campaign.id === pledge.pledge_campaign.id,
    ),
  )

  const concludedPledges = pledges.filter(
    (pledge) => !currentPledges.includes(pledge),
  )

  const handleConcludedPledgesToggle = (event: {
    currentTarget: { open: boolean }
  }) => setShowConcludedPledges(event.currentTarget.open)

  return (
    <div className="d-f fd-c g-2">
      {currentPledges.length > 0 && (
        <div className="d-f fd-c g-1">
          <div className="fw-600 fs-13 tt-u">Current pledges</div>
          {currentPledges.map((pledge) => (
            <PledgeDetails
              key={pledge.id}
              jointGiver={jointGiver}
              pledge={pledge}
              pledgeCampaign={pledge.pledge_campaign}
              fund={pledge.fund}
              showFullDetails={showFullDetails}
            />
          ))}
        </div>
      )}
      {concludedPledges.length > 0 && (
        <details
          onToggle={handleConcludedPledgesToggle}
          open={showConcludedPledges}
          className="d-f fd-c g-1"
          css={css`
            &[open] .pledges-list {
              max-height: 100%;
            }
          `}
        >
          <summary
            className="d-f ai-c jc-sb"
            css={css`
              cursor: pointer;
            `}
          >
            <div className="d-f g-1 ai-c">
              <span className="fw-600 fs-13 tt-u">Concluded pledges</span>
              <span
                className="fs-4 fw-600"
                css={css`
                  background-color: var(--color-tint3);
                  color: var(--color-tint10);
                  border-radius: 40px;
                  padding-right: 10px;
                  padding-left: 10px;
                `}
              >{`${concludedPledges.length}`}</span>
            </div>
            <Icon
              symbol={`general#${showConcludedPledges ? "down" : "up"}-chevron`}
              aria-hidden="true"
              css={css`
                height: 12px;
                width: 12px;
                fill: var(--color-static-gray45);
              `}
            />
          </summary>
          <div
            className="pledges-list"
            css={css`
              max-height: 0px;
              overflow: hidden;
              transition: max-height 0.3s ease;
            `}
          >
            {concludedPledges.map((pledge) => (
              <PledgeDetails
                key={pledge.id}
                jointGiver={jointGiver}
                pledge={pledge}
                pledgeCampaign={pledge.pledge_campaign}
                fund={pledge.fund}
                showFullDetails={showFullDetails}
              />
            ))}
          </div>
        </details>
      )}
    </div>
  )
}
